import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// SECURITY + RESILIENCE notes:
// • Primary auth: httpOnly + secure + SameSite=None cookie set by the backend.
// • Problem 1: Safari, Firefox-ETP and Brave block third-party cookies by
//   default. When the frontend (e.g. www.harmonix.info) and backend (e.g.
//   api.harmonix.info) are on different sub-domains the cookie can be
//   silently dropped, breaking auth.
// • Problem 2: a single JS-memory bearer fallback DOESN'T survive across
//   browser tabs — opening any internal link in a new tab spawned a fresh
//   JS context with no token, causing the new tab to bounce login↔dashboard
//   until /auth/me settled.
// • Resilience: we now persist the session_token in localStorage so:
//     – New tabs / refreshes immediately pick up the token via the Bearer
//       header (no race with the cookie).
//     – `Sign out` clears it; a server 401 on /auth/me clears it (treated
//       as definitive logout).
//   Trade-off: localStorage is readable by same-origin JS so a successful
//   XSS could exfiltrate it. We accept this trade-off because (a) the
//   cookie is still the primary credential, (b) Harmonix has CSP + frame-
//   ancestors + strict input sanitization, (c) users explicitly asked for
//   the "stay logged in across tabs" Facebook/Google behaviour.
const SESSION_TOKEN_KEY = 'harmonix_st';

const safeGetToken = () => {
  try { return localStorage.getItem(SESSION_TOKEN_KEY) || null; }
  catch { return null; }
};
const safeSetToken = (v) => {
  try {
    if (v) localStorage.setItem(SESSION_TOKEN_KEY, v);
    else localStorage.removeItem(SESSION_TOKEN_KEY);
  } catch { /* ignore */ }
};

let inMemorySessionToken = safeGetToken();
export const _setSessionTokenFromResponse = (token) => {
  inMemorySessionToken = token || null;
  safeSetToken(token);
};
export const _clearSessionToken = () => {
  inMemorySessionToken = null;
  safeSetToken(null);
};

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor — attach Bearer fallback (cookie still preferred when present)
api.interceptors.request.use((config) => {
  if (inMemorySessionToken) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${inMemorySessionToken}`;
  }
  return config;
});

// Response interceptor — if a fresh session_token is returned, remember it
api.interceptors.response.use((response) => {
  const token = response?.data?.session_token;
  if (token && typeof token === 'string' && token.startsWith('sess_')) {
    inMemorySessionToken = token;
    safeSetToken(token);
  }
  return response;
}, (error) => {
  // ONLY treat a 401 on the auth-validation endpoints as a hard logout.
  // 401s on other endpoints (e.g. a permissions-denied admin call) should
  // NOT wipe the session, otherwise a single denied request kicks the
  // user back to /login.
  const url = error?.config?.url || '';
  const isAuthValidationCall = /\/auth\/(me|permissions|feature-access)\b/.test(url);
  if (error.response?.status === 401 && isAuthValidationCall) {
    try {
      localStorage.removeItem('harmonix_user');
      localStorage.removeItem('harmonix_features');
    } catch { /* localStorage unavailable in some environments */ }
    _clearSessionToken();
  }
  return Promise.reject(error);
});

// Auth API
export const authAPI = {
  exchangeSession: (sessionId) => 
    api.post('/auth/session', { session_id: sessionId }),
  
  getMe: () => api.get('/auth/me'),
  
  getFeatureAccess: () => api.get('/auth/feature-access'),

  getMyPermissions: () => api.get('/auth/permissions'),
  
  logout: async () => {
    try { return await api.post('/auth/logout'); }
    finally { inMemorySessionToken = null; }
  },
  
  login: (email, password) => 
    api.post('/auth/login', { email, password }),
  
  register: (name, email, password, accountType = 'customer', referralCode = null) => 
    api.post('/auth/register', { name, email, password, account_type: accountType, referral_code: referralCode }),
  
  forgotPassword: (email) => 
    api.post('/auth/forgot-password', { email }),
  
  resetPassword: (token, newPassword) => 
    api.post('/auth/reset-password', { token, new_password: newPassword }),
  
  // Helper kept for backwards compatibility
  clearSession: () => {
    _clearSessionToken();
    try {
      localStorage.removeItem('harmonix_user');
      localStorage.removeItem('harmonix_features');
    } catch { /* ignore */ }
  },
};

// Accounts API
export const accountsAPI = {
  list: () => api.get('/accounts'),
  
  create: (data) => api.post('/accounts', data),
  
  get: (accountId) => api.get(`/accounts/${accountId}`),
  
  delete: (accountId) => api.delete(`/accounts/${accountId}`),
  
  // Sub-accounts
  listSubAccounts: (accountId) => 
    api.get(`/accounts/${accountId}/sub-accounts`),
  
  createSubAccount: (accountId, data) => 
    api.post(`/accounts/${accountId}/sub-accounts`, data),
  
  getSubAccount: (accountId, subAccountId) => 
    api.get(`/accounts/${accountId}/sub-accounts/${subAccountId}`),
  
  deleteSubAccount: (accountId, subAccountId) => 
    api.delete(`/accounts/${accountId}/sub-accounts/${subAccountId}`),
};

// Chatbots API
export const chatbotsAPI = {
  listAll: () => api.get('/chatbots'),
  
  list: (accountId, subAccountId) => 
    api.get(`/accounts/${accountId}/sub-accounts/${subAccountId}/chatbots`),
  
  create: (accountId, subAccountId, data) => 
    api.post(`/accounts/${accountId}/sub-accounts/${subAccountId}/chatbots`, data),
  
  get: (accountId, subAccountId, chatbotId) => 
    api.get(`/accounts/${accountId}/sub-accounts/${subAccountId}/chatbots/${chatbotId}`),
  
  update: (accountId, subAccountId, chatbotId, data) => 
    api.patch(`/accounts/${accountId}/sub-accounts/${subAccountId}/chatbots/${chatbotId}`, data),
  
  delete: (accountId, subAccountId, chatbotId) => 
    api.delete(`/accounts/${accountId}/sub-accounts/${subAccountId}/chatbots/${chatbotId}`),
};

// Knowledge Base API
export const knowledgeAPI = {
  listDocuments: (accountId, subAccountId, chatbotId) => 
    api.get(`/accounts/${accountId}/sub-accounts/${subAccountId}/chatbots/${chatbotId}/documents`),
  
  uploadDocument: (accountId, subAccountId, chatbotId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(
      `/accounts/${accountId}/sub-accounts/${subAccountId}/chatbots/${chatbotId}/documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  
  addTextDocument: (accountId, subAccountId, chatbotId, data) => 
    api.post(`/accounts/${accountId}/sub-accounts/${subAccountId}/chatbots/${chatbotId}/documents/text`, data),
  
  getDocument: (accountId, subAccountId, chatbotId, documentId, includeContent = false) => 
    api.get(`/accounts/${accountId}/sub-accounts/${subAccountId}/chatbots/${chatbotId}/documents/${documentId}?include_content=${includeContent}`),
  
  deleteDocument: (accountId, subAccountId, chatbotId, documentId) => 
    api.delete(`/accounts/${accountId}/sub-accounts/${subAccountId}/chatbots/${chatbotId}/documents/${documentId}`),
  
  // API Sources
  listAPISources: (accountId, subAccountId, chatbotId) => 
    api.get(`/accounts/${accountId}/sub-accounts/${subAccountId}/chatbots/${chatbotId}/api-sources`),
  
  createAPISource: (accountId, subAccountId, chatbotId, data) => 
    api.post(`/accounts/${accountId}/sub-accounts/${subAccountId}/chatbots/${chatbotId}/api-sources`, data),
  
  deleteAPISource: (accountId, subAccountId, chatbotId, sourceId) => 
    api.delete(`/accounts/${accountId}/sub-accounts/${subAccountId}/chatbots/${chatbotId}/api-sources/${sourceId}`),
};

// Analytics API
export const analyticsAPI = {
  getChatbotOverview: (chatbotId) => 
    api.get(`/analytics/chatbot/${chatbotId}/overview`),
  
  getRecentConversations: (chatbotId, limit = 10) => 
    api.get(`/analytics/chatbot/${chatbotId}/recent-conversations?limit=${limit}`),
};

// Team API
export const teamAPI = {
  listMembers: (accountId) => 
    api.get(`/accounts/${accountId}/team`),
  
  inviteMember: (accountId, data) => 
    api.post(`/accounts/${accountId}/team/invite`, data),
  
  listInvitations: (accountId) => 
    api.get(`/accounts/${accountId}/team/invitations`),
  
  cancelInvitation: (accountId, invitationId) => 
    api.delete(`/accounts/${accountId}/team/invitations/${invitationId}`),
  
  updateRole: (accountId, memberId, role) => 
    api.patch(`/accounts/${accountId}/team/members/${memberId}/role`, { role }),
  
  removeMember: (accountId, memberId) => 
    api.delete(`/accounts/${accountId}/team/members/${memberId}`),
  
  acceptInvitation: (token) => 
    api.post(`/invitations/accept/${token}`),
};

// Export API
export const exportAPI = {
  exportConversations: (chatbotId, format = 'json') => 
    api.get(`/export/chatbot/${chatbotId}/conversations?format=${format}`, { responseType: 'blob' }),
  
  exportAnalytics: (chatbotId, format = 'json') => 
    api.get(`/export/chatbot/${chatbotId}/analytics?format=${format}`, { responseType: format === 'csv' ? 'blob' : 'json' }),
};

// Billing API
export const billingAPI = {
  getPlans: () => api.get('/billing/plans'),
  
  getSubscription: () => api.get('/billing/subscription'),
  
  createCheckout: (tier, billingCycle, originUrl) => 
    api.post('/billing/checkout', { tier, billing_cycle: billingCycle, origin_url: originUrl }),
  
  getCheckoutStatus: (sessionId) => 
    api.get(`/billing/checkout/status/${sessionId}`),
  
  getUsage: () => api.get('/billing/usage'),
};

// Webhooks API
export const webhooksAPI = {
  list: (chatbotId) => api.get(`/chatbots/${chatbotId}/webhooks`),
  
  create: (chatbotId, data) => api.post(`/chatbots/${chatbotId}/webhooks`, data),
  
  update: (chatbotId, webhookId, data) => 
    api.patch(`/chatbots/${chatbotId}/webhooks/${webhookId}`, data),
  
  delete: (chatbotId, webhookId) => 
    api.delete(`/chatbots/${chatbotId}/webhooks/${webhookId}`),
  
  test: (chatbotId, webhookId) => 
    api.post(`/chatbots/${chatbotId}/webhooks/${webhookId}/test`),
};

// Branding API
export const brandingAPI = {
  get: (accountId) => api.get(`/accounts/${accountId}/branding`),
  
  update: (accountId, data) => api.patch(`/accounts/${accountId}/branding`, data),
  
  uploadLogo: (accountId, file, logoType) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(
      `/accounts/${accountId}/branding/logo?logo_type=${logoType}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
};

// Moderators API
export const moderatorsAPI = {
  list: (chatbotId) => api.get(`/chatbots/${chatbotId}/moderators`),
  
  add: (chatbotId, email) => api.post(`/chatbots/${chatbotId}/moderators`, { email }),
  
  remove: (chatbotId, moderatorId) => api.delete(`/chatbots/${chatbotId}/moderators/${moderatorId}`),
  
  // Get chatbots the current user can moderate
  getModeratedChatbots: () => api.get('/moderator/chatbots'),
  
  // Check if current user has access to a chatbot
  checkAccess: (chatbotId) => api.get(`/moderator/check/${chatbotId}`),
};

// Conversations API
export const conversationsAPI = {
  // List conversations with pagination and filters
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.chatbot_id) queryParams.append('chatbot_id', params.chatbot_id);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    if (params.search) queryParams.append('search', params.search);
    if (params.sentiment) queryParams.append('sentiment', params.sentiment);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    return api.get(`/conversations?${queryParams.toString()}`);
  },
  
  // Get full conversation details
  getDetail: (sessionId) => api.get(`/conversations/${sessionId}`),
  
  // Get analytics for a chatbot
  getAnalytics: (chatbotId, days = 30) => api.get(`/conversations/analytics/${chatbotId}?days=${days}`),
  
  // Generate AI summary for a conversation
  summarize: (sessionId) => api.post(`/conversations/${sessionId}/summarize`),
  
  // Delete a conversation (admin only)
  delete: (sessionId) => api.delete(`/conversations/${sessionId}`),
};

// Shortlinks API
export const shortlinksAPI = {
  // Create a shortlink
  create: (url, linkType = 'general', expiresInHours = null) => 
    api.post('/shortlinks', { url, link_type: linkType, expires_in_hours: expiresInHours }),
  
  // Get shortlink stats
  getStats: (shortCode) => api.get(`/shortlinks/${shortCode}/stats`),
  
  // Helper to get the shortened URL from base
  getShortUrl: (shortCode) => `${process.env.REACT_APP_BACKEND_URL}/s/${shortCode}`,
};

// Channels API
export const channelsAPI = {
  getChannels: (chatbotId) => api.get(`/chatbots/${chatbotId}/channels`),
  saveChannel: (chatbotId, channelType, config) => api.post(`/chatbots/${chatbotId}/channels/${channelType}`, config),
  disconnectChannel: (chatbotId, channelType) => api.delete(`/chatbots/${chatbotId}/channels/${channelType}`),
  testChannel: (chatbotId, channelType) => api.get(`/chatbots/${chatbotId}/channels/${channelType}/test`),
};

// Calendar Smart Schedule API
export const calendarAPI = {
  getSmartSchedule: (chatbotId) => api.get(`/calendar/chatbot/${chatbotId}/smart-schedule`),
  getConnections: (chatbotId) => api.get(`/calendar/chatbot/${chatbotId}/connections`),
  disconnectCalendar: (chatbotId, connectionId) => api.delete(`/calendar/chatbot/${chatbotId}/connection/${connectionId}`),
  getAvailability: (chatbotId, date) => api.get(`/calendar/chatbot/${chatbotId}/availability?date=${date}`),
};

// RBAC (Role & Permission Management)
export const rbacAPI = {
  getSegments: () => api.get('/admin/rbac/segments'),
  listRoles: () => api.get('/admin/rbac/roles'),
  createRole: (data) => api.post('/admin/rbac/roles', data),
  updateRole: (roleId, data) => api.patch(`/admin/rbac/roles/${roleId}`, data),
  deleteRole: (roleId) => api.delete(`/admin/rbac/roles/${roleId}`),
  duplicateRole: (roleId, data) => api.post(`/admin/rbac/roles/${roleId}/duplicate`, data),
  bulkAssignRole: (userIds, roleId) => api.post('/admin/rbac/users/bulk-assign-role', { user_ids: userIds, role_id: roleId }),
  assignUserRole: (userId, roleId) => api.patch(`/admin/rbac/users/${userId}/role`, { role_id: roleId }),
  setUserOverrides: (userId, overrides) => api.patch(`/admin/rbac/users/${userId}/overrides`, { overrides }),
  getUserPermissions: (userId) => api.get(`/admin/rbac/users/${userId}/permissions`),
};

export default api;
